import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';

/**
 * Load one @api registry entry: { data, error, loading, reload, setData }.
 * `data` is the response body (usually { data, ... }); pass `select` to unwrap.
 * Re-runs when `params` change (compared by JSON). Works the same for real
 * endpoints and fixtures.
 *
 *   const { data: orders, loading, error, reload } = useQuery('merchantOrders', { status }, { select: (r) => r.data });
 */
export function useQuery(apiKey, params, { select = (r) => r, enabled = true } = {}) {
  const { fetchApi } = useAppApi();
  // `ran` is set once a fetch has settled. Until then an enabled query reports loading, so the
  // render between `enabled` turning true and the effect starting is never { loading: false, data: undefined }.
  const [state, setState] = useState({ data: undefined, error: '', loading: enabled, ran: false });
  const key = JSON.stringify(params ?? null);
  const selectRef = useRef(select);
  selectRef.current = select;

  const run = useCallback(async () => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await fetchApi(apiKey, params, false);
      setState({ data: selectRef.current(res), error: '', loading: false, ran: true });
    } catch (ex) {
      setState({ data: undefined, error: errorText(ex), loading: false, ran: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, key, enabled]);

  useEffect(() => {
    run();
  }, [run]);

  const setData = (updater) => setState((s) => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  const { ran, ...rest } = state;
  return { ...rest, loading: rest.loading || (enabled && !ran), reload: run, setData };
}

/**
 * Fire one @api registry entry on demand: { run, loading, error }.
 * `run(params)` resolves to the response body or throws (error is also set).
 */
export function useMutation(apiKey) {
  const { fetchApi } = useAppApi();
  const [state, setState] = useState({ loading: false, error: '' });
  const run = async (params) => {
    setState({ loading: true, error: '' });
    try {
      const res = await fetchApi(apiKey, params, false);
      setState({ loading: false, error: '' });
      return res;
    } catch (ex) {
      setState({ loading: false, error: errorText(ex) });
      throw ex;
    }
  };
  return { ...state, run };
}
